import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import { httpsCallable } from 'firebase/functions';
import { CartItem, EndOrderDetails, Order } from '@pedix-workspace/utils';
import { collection, doc, Firestore, getDoc } from 'firebase/firestore';
import { catchError, map } from 'rxjs/operators';
import { Observable, from, of } from 'rxjs';
import { getOrderConverter, getOrderSnapshotConverter } from '@pedix-workspace/shared-models';
import { AppRequestContextService } from '../../app-request-context.service';
import { EstablishmentsCheckoutService } from './establishments-checkout.service';
import { StockService } from '@pedix-workspace/pedixapp-stock';
import { mergeProductStockRecords, ProductStockRecord } from '@pedix-workspace/shared-stock';
import {
  isOrderPricingError,
  isOrderStockError,
  isProductRestrictionError,
  isPromotionConditionsChangedError,
} from '@pedix-workspace/angular-shared-order';
import { FIREBASE_FIRESTORE, FIREBASE_FUNCTIONS } from '@pedix-workspace/angular-firebase';

type CreateOrderResult = {
  orderId: string;
  sequenceId: number;
  /**
   * Stock post-venta de las entidades tocadas, absoluto y de transaction-time. Opcional porque una
   * versión anterior del callable no lo devuelve (ventana de rollout).
   */
  updatedProductStock?: ProductStockRecord[];
};

@Injectable({
  providedIn: 'root',
})
export class OrderCheckoutService {
  endOrderInMemory: EndOrderDetails;

  get ordersCollectionRef() {
    return collection(this.firestore, 'orders').withConverter(getOrderConverter());
  }

  get orderSnapshotCollectionRef() {
    return collection(this.firestore, 'ordersnapshot').withConverter(getOrderSnapshotConverter());
  }

  private firestore = inject(FIREBASE_FIRESTORE);
  private functions = inject(FIREBASE_FUNCTIONS);
  private appRequestContext = inject(AppRequestContextService);
  private establishmentsCheckoutService = inject(EstablishmentsCheckoutService);
  private stockService = inject(StockService);
  private injector = inject(Injector);

  /**
   * El stock local NO se refetchea acá: `/stock` lee la réplica `enterprise` (eventualmente
   * consistente), así que pedirlo justo después de crear el pedido devuelve los valores PRE-pedido.
   * Las dos vías aplican valores absolutos de transaction-time que devuelve el propio callable.
   */
  async save(endOrderDetails: EndOrderDetails): Promise<CreateOrderResult> {
    const createOrderFn = httpsCallable<EndOrderDetails, CreateOrderResult>(
      this.functions,
      'createOrder',
    );

    try {
      const result = await createOrderFn(endOrderDetails);

      this.stockService.productStock.update(currentStock =>
        mergeProductStockRecords(currentStock, result.data.updatedProductStock ?? []),
      );

      return result.data;
    } catch (error: unknown) {
      if (isOrderStockError(error)) {
        const freshRecords = error.details.stockErrors
          .map(stockError => stockError.item)
          .filter((item): item is ProductStockRecord => item !== undefined);

        this.stockService.productStock.update(currentStock =>
          mergeProductStockRecords(currentStock, freshRecords),
        );
      }
      if (isOrderPricingError(error) || isPromotionConditionsChangedError(error)) {
        await this.appRequestContext.refreshCatalogState();
      }
      if (isProductRestrictionError(error)) {
        await Promise.all([
          this.appRequestContext.refreshCatalogState().catch(() => null),
          this.establishmentsCheckoutService
            .refresh(this.appRequestContext.establishment().id)
            .catch(() => null),
        ]);
      }
      throw error;
    }
  }

  getOrderDetails(orderId: string): Observable<EndOrderDetails | null> {
    if (this.endOrderInMemory && this.endOrderInMemory.id === orderId) {
      return of(this.endOrderInMemory);
    }

    const docPromise = runInInjectionContext(this.injector, () =>
      getDoc(doc(this.orderSnapshotCollectionRef, orderId)),
    );

    return from(docPromise).pipe(
      map(snapshot => (snapshot.exists() ? snapshot.data() : null)),
      catchError((error: unknown) => {
        console.error(error);

        return of(null);
      }),
    );
  }

  getOrderCartItems(orderId: string, establishmentId: string): Observable<CartItem[] | null> {
    return this.getOrderDetails(orderId).pipe(
      map(order => {
        if (!order || order.establishmentId !== establishmentId) {
          return null;
        }

        return order.cartItems ?? [];
      }),
    );
  }

  getOrder(orderId: string): Observable<Order | null> {
    const docPromise = runInInjectionContext(this.injector, () =>
      getDoc(doc(this.ordersCollectionRef, orderId)),
    );

    return from(docPromise).pipe(
      map(snapshot => (snapshot.exists() ? snapshot.data() : null)),
      catchError((error: unknown) => {
        console.error(error);

        return of(null);
      }),
    );
  }
}
